define('Contactus.ei1.Main.View', [
    'SCView',
    'contact_us.tpl',
    'Contactus.ei1.Main.Model'
], function (
    SCViewComponent,
    contact_us_tpl,
    ContactusModel
) {
    'use strict';

    var SCView = SCViewComponent.SCView;

    // Loose validation only - catches obviously-malformed input before it
    // reaches the backend, not full RFC/E.164 compliance. The backend
    // Model re-checks these as a backstop in case JS is bypassed.
    var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var PHONE_REGEX = /^[0-9()+\-.\s]{7,20}$/;

    // *** Use the SAME reCAPTCHA v3 site key already in the GeneralInquiry
    // form's View.js (public - safe to ship in frontend JS). It's the same
    // domain registration (shop.1homeoffice.com), so the same key pair
    // works for both forms; only the action label below differs, which is
    // just for analytics in the reCAPTCHA admin console. Until this is a
    // real key, Google's script fails to load/execute and reCAPTCHA is
    // effectively skipped client-side (the backend's placeholder check
    // handles that gracefully too - see the Model.js SuiteScript2 file).
    var RECAPTCHA_SITE_KEY = '6LfWpr4tAAAAAH_BqwL7-1XMFTpIoRKrSamXbesB';
    var RECAPTCHA_ACTION = 'contact_us_submit';

    var recaptchaScriptPromise = null;

    // Lazily injects Google's reCAPTCHA v3 script exactly once (regardless
    // of how many times the form is submitted or re-rendered), and
    // resolves once window.grecaptcha is actually ready to use. Loaded as
    // a plain <script> tag rather than an AMD module, since it's a
    // third-party global-exposing script, not AMD-compatible.
    //
    // NOTE: if the site's Content Security Policy blocks script sources
    // other than the ones already allow-listed for this NetSuite account,
    // this script will fail to load - that's a NetSuite/SMT-side setting
    // (Content Security Policy / allowed domains), not something fixable
    // from this file. If reCAPTCHA silently never triggers, check that
    // first before assuming a code bug.
    function loadRecaptchaScript() {
        if (recaptchaScriptPromise) { return recaptchaScriptPromise; }

        recaptchaScriptPromise = new jQuery.Deferred();

        if (window.grecaptcha && window.grecaptcha.execute) {
            recaptchaScriptPromise.resolve();
            return recaptchaScriptPromise;
        }

        var script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=' + RECAPTCHA_SITE_KEY;
        script.async = true;
        script.onload = function () {
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(function () { recaptchaScriptPromise.resolve(); });
            } else {
                recaptchaScriptPromise.reject();
            }
        };
        script.onerror = function () { recaptchaScriptPromise.reject(); };
        document.head.appendChild(script);

        return recaptchaScriptPromise;
    }

    // Every field maps to: the formData key, the data-group used to flag
    // the field visually on error, whether it's required, and an optional
    // format test. All fields are required here (each is marked * in the
    // design), but keeping `required` explicit makes it trivial to relax
    // one later without touching the loop.
    var FIELDS = [
        { key: 'firstName', group: 'firstName', required: true, message: 'First name is required.' },
        { key: 'lastName', group: 'lastName', required: true, message: 'Last name is required.' },
        {
            key: 'email', group: 'email', required: true, message: 'Email is required.',
            formatTest: function (v) { return EMAIL_REGEX.test(v); },
            formatMessage: 'Please enter a valid email address.'
        },
        {
            key: 'phone', group: 'phone', required: true, message: 'Phone number is required.',
            formatTest: function (v) { return PHONE_REGEX.test(v); },
            formatMessage: 'Please enter a valid phone number.'
        },
        { key: 'zip', group: 'zip', required: true, message: 'Zip code is required.' },
        { key: 'address', group: 'address', required: true, message: 'Address is required.' },
        // Optional - no format test, never blocks submit. Kept in FIELDS
        // (not skipped) so it's cleared by _resetForm and re-fed on
        // re-render like every other field.
        { key: 'comments', group: 'comments', required: false }
    ];

    // ===== ANALYTICS (GTM / GA4) =====
    // Pushes a custom event to GTM's dataLayer. GTM's built-in "Form
    // Submission" trigger can't see this form (it's an AJAX save inside
    // the SPA, never a native <form> submit), so GTM listens for these
    // custom events instead via a Custom Event trigger.
    //
    // Never push PII here (name, email, phone, zip, address, comments) -
    // Google Analytics' terms prohibit it. Only non-identifying context.
    var FORM_NAME = 'contact_us';

    function pushDataLayer(eventName, params) {
        if (typeof window === 'undefined') { return; } // SSP/SEO safety
        try {
            window.dataLayer = window.dataLayer || [];
            var payload = { event: eventName, form_name: FORM_NAME, form_location: window.location.pathname };
            if (params) {
                for (var k in params) {
                    if (Object.prototype.hasOwnProperty.call(params, k)) { payload[k] = params[k]; }
                }
            }
            window.dataLayer.push(payload);
        } catch (err) { /* tracking must never break the form */ }
    }

    var self = null;

    function isEmpty(value) {
        return value === undefined || value === null || String(value).trim() === '';
    }

    // ===== CONSTRUCTOR =====
    function ContactusMainView(options) {
        SCView.call(this);
        self = this;
        self.options = options || {};
        self.template = contact_us_tpl;

        self.model = new ContactusModel();
        self.isSubmitting = false;
        self.formData = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            zip: '',
            address: '',
            comments: ''
        };
    }

    ContactusMainView.prototype = Object.create(SCView.prototype);
    ContactusMainView.prototype.constructor = ContactusMainView;

    ContactusMainView.prototype.getEvents = function () {
        return {
            'change .js-cf-input': 'onFieldChange',
            'keyup .js-cf-input': 'onFieldChange',
            'click [data-action="submit"]': 'onSubmitClick'
        };
    };

    ContactusMainView.prototype.getContext = function () {
        // The template hardcodes labels/placeholders (they're fixed in the
        // design), so getContext only needs to feed current values back so
        // the fields survive a re-render, plus the disabled/submitting flag.
        return {
            selected: self.formData,
            isSubmitting: self.isSubmitting
        };
    };

    ContactusMainView.prototype.onFieldChange = function (e) {
        var $el = self.$(e.currentTarget);
        var field = $el.data('field');
        if (!field) { return; }
        self.formData[field] = $el.val();
        self._clearGroupError(field);
        self._hideBanners();
    };

    // ===== VALIDATION =====

    ContactusMainView.prototype._clearGroupError = function (group) {
        self.$('[data-group="' + group + '"]').removeClass('has-error');
    };

    ContactusMainView.prototype._showGroupError = function (group, message) {
        var $group = self.$('[data-group="' + group + '"]');
        $group.addClass('has-error');
        if (message) {
            $group.find('.cf-error').text(message);
        }
    };

    ContactusMainView.prototype.validateForm = function () {
        var firstInvalidEl = null;

        FIELDS.forEach(function (cfg) {
            self._clearGroupError(cfg.group);
        });

        FIELDS.forEach(function (cfg) {
            var value = self.formData[cfg.key];
            var empty = isEmpty(value);

            if (cfg.required && empty) {
                self._showGroupError(cfg.group, cfg.message);
                if (!firstInvalidEl) { firstInvalidEl = self.$('[data-group="' + cfg.group + '"]'); }
                return;
            }

            if (!empty && cfg.formatTest && !cfg.formatTest(value)) {
                self._showGroupError(cfg.group, cfg.formatMessage);
                if (!firstInvalidEl) { firstInvalidEl = self.$('[data-group="' + cfg.group + '"]'); }
            }
        });

        if (firstInvalidEl && firstInvalidEl.length) {
            firstInvalidEl[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }
        return true;
    };

    // ===== BANNERS =====

    ContactusMainView.prototype._hideBanners = function () {
        self.$('.js-cf-success').removeClass('is-visible');
        self.$('.js-cf-error').removeClass('is-visible');
    };

    ContactusMainView.prototype._showSuccess = function () {
        self.$('.js-cf-error').removeClass('is-visible');
        self.$('.js-cf-success').addClass('is-visible');
    };

    ContactusMainView.prototype._showError = function (message) {
        self.$('.js-cf-success').removeClass('is-visible');
        if (message) { self.$('.js-cf-error').text(message); }
        self.$('.js-cf-error').addClass('is-visible');
    };

    ContactusMainView.prototype._setSubmitting = function (submitting) {
        self.isSubmitting = submitting;
        var $btn = self.$('[data-action="submit"]');
        $btn.prop('disabled', submitting);
        $btn.toggleClass('is-loading', submitting);
    };

    ContactusMainView.prototype._resetForm = function () {
        FIELDS.forEach(function (cfg) {
            self.formData[cfg.key] = '';
        });
        self.$('.js-cf-input').val('');
    };

    // ===== SUBMIT =====

    ContactusMainView.prototype.onSubmitClick = function (e) {
        e.preventDefault();
        if (self.isSubmitting) { return; }

        if (!self.validateForm()) { return; }

        self._setSubmitting(true);
        self._hideBanners();

        // Fetch a fresh reCAPTCHA v3 token right before submitting (tokens
        // are short-lived and single-use, so this can't be done earlier
        // and cached). If the script/site key isn't set up correctly,
        // proceed WITHOUT a token rather than blocking the visitor
        // entirely - the backend's own placeholder-aware check handles a
        // missing token the same permissive way while it's still being
        // configured (see RECAPTCHA_SECRET_KEY in the backend Model.js).
        //
        // .done()/.fail() (not .then()) on the jQuery Deferred wrapper
        // deliberately, since jQuery's .then() doesn't reliably assimilate
        // a native Promise (what grecaptcha.execute() returns) across all
        // jQuery versions - .done() only ever receives plain values, so
        // there's nothing to assimilate.
        loadRecaptchaScript().done(function () {
            window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION })
                .then(
                    function (token) { self._submitWithToken(token); },
                    function () { self._submitWithToken(null); }
                );
        }).fail(function () {
            self._submitWithToken(null);
        });
    };

    ContactusMainView.prototype._submitWithToken = function (recaptchaToken) {
        var payload = {
            firstName: self.formData.firstName,
            lastName: self.formData.lastName,
            email: self.formData.email,
            phone: self.formData.phone,
            zip: self.formData.zip,
            address: self.formData.address,
            comments: self.formData.comments,
            recaptchaToken: recaptchaToken,
            recaptchaAction: RECAPTCHA_ACTION
        };

        self.model.save(payload, {
            type: 'POST',
            success: function (model, response) {
                self._setSubmitting(false);
                if (response && response.success && response.data && response.data.internalId) {
                    // Fire BEFORE _resetForm so comments presence is still known.
                    pushDataLayer('form_submit_success', {
                        has_comments: !isEmpty(self.formData.comments)
                    });
                    self._resetForm();
                    self._showSuccess();
                } else {
                    pushDataLayer('form_submit_error', { error_type: 'server_rejected' });
                    self._showError((response && response.message) || 'Something went wrong. Please try again.');
                }
            },
            error: function (model, xhr) {
                self._setSubmitting(false);
                pushDataLayer('form_submit_error', {
                    error_type: 'request_failed',
                    http_status: (xhr && xhr.status) || 0
                });
                var message = 'Something went wrong. Please try again.';
                try {
                    var parsed = JSON.parse(xhr.responseText);
                    if (parsed && parsed.message) { message = parsed.message; }
                } catch (err) { /* keep default */ }
                self._showError(message);
            }
        });
    };

    return ContactusMainView;
});
